import type { VoidFunctionComponent } from 'react';

import Button, { NavigationButton } from '../../../generic/Button';
import { icons, IconText } from '../../../generic/Icon';
import { UnlinkIssuesModalDetailsContainer } from '../../modals';
import { useIssueDetailsModals } from '../IssueDetailsModalsContext';

import type { ActionButtonAndModalProps } from './shared';

type Props = ActionButtonAndModalProps & {
  //  Optional because if we are in the global scope,
  //  there may be multiple jira tickets, so it doesn't
  //  make sense to show `n` number of buttons for each ticket.
  jiraTicketURL?: string;
  //  Also optional, be because it is only used in the global
  //  scope to indicate that multiple issues are being unlinked.
  jiraTicketCount?: number;
};

/**
 * IssueDetailsActionButtonUnlink includes the button and the modal
 * for the unlink action on the issue details page and drawer. The modal state is managed
 * by useIssueDetailsModals, which passes the modal state down as context. Go see the
 * IssueDetailsModalsContext for more info.
 */
const IssueDetailsActionButtonUnlink: VoidFunctionComponent<Props> = ({
  id,
  jiraTicketURL,
  jiraTicketCount,
}) => {
  const { unlinkModal, isAnyModalOpen } = useIssueDetailsModals();
  return (
    <>
      {jiraTicketURL && (
        <NavigationButton
          type="secondary"
          size="medium"
          to={jiraTicketURL}
          isDisabled={isAnyModalOpen}
          openInNewTab
        >
          <IconText
            text="View on Jira"
            icon={icons.externalLink}
            alignIcon="right"
          />
        </NavigationButton>
      )}
      <Button
        type="secondary"
        size="medium"
        onClick={unlinkModal.onOpen}
        isDisabled={isAnyModalOpen}
      >
        Unlink
      </Button>
      <UnlinkIssuesModalDetailsContainer
        id={id}
        show={unlinkModal.isOpen}
        onHide={unlinkModal.onClose}
        jiraTicketCount={jiraTicketCount}
      />
    </>
  );
};

export default IssueDetailsActionButtonUnlink;
